import React, { useState, useRef } from "react";
import { useOrganization } from "@clerk/clerk-react";
import { DashboardLayout } from "@/components/dashboard/dashboard-layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { Briefcase, Camera, Loader2, Save, Upload, Building2 } from "lucide-react";

export default function CompanyPage() {
  const { toast } = useToast();
  const { organization, isLoaded } = useOrganization();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [name, setName] = useState(organization?.name || "");
  const [slug, setSlug] = useState(organization?.slug || "");
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);

  // Update local state when organization loads
  React.useEffect(() => {
    if (organization) {
      setName(organization.name || "");
      setSlug(organization.slug || "");
    }
  }, [organization]);

  const handleSave = async () => {
    if (!organization) return;

    setIsSaving(true);
    try {
      await organization.update({
        name: name.trim(),
        slug: slug.trim().toLowerCase().replace(/\s+/g, "-"),
      });
      toast({
        title: "Company updated",
        description: "Your company information has been saved successfully.",
      });
    } catch (error) {
      toast({
        title: "Failed to update company",
        description: error instanceof Error ? error.message : "An error occurred while saving.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !organization) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast({
        title: "Invalid file type",
        description: "Please upload an image file (PNG, JPG, GIF, etc.)",
        variant: "destructive",
      });
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Please upload an image smaller than 10MB.",
        variant: "destructive",
      });
      return;
    }

    setIsUploadingLogo(true);
    try {
      await organization.setLogo({ file });
      toast({
        title: "Avatar updated",
        description: "Your company avatar has been updated successfully.",
      });
    } catch (error) {
      toast({
        title: "Failed to upload avatar",
        description: error instanceof Error ? error.message : "An error occurred while uploading.",
        variant: "destructive",
      });
    } finally {
      setIsUploadingLogo(false);
      // Reset the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleRemoveLogo = async () => {
    if (!organization) return;

    setIsUploadingLogo(true);
    try {
      await organization.setLogo({ file: null });
      toast({
        title: "Avatar removed",
        description: "Your company avatar has been removed.",
      });
    } catch (error) {
      toast({
        title: "Failed to remove avatar",
        description: error instanceof Error ? error.message : "An error occurred.",
        variant: "destructive",
      });
    } finally {
      setIsUploadingLogo(false);
    }
  };

  if (!isLoaded) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      </DashboardLayout>
    );
  }

  if (!organization) {
    return (
      <DashboardLayout>
        <div className="py-12 text-center text-gray-500">
          No organization found. Please create or join an organization first.
        </div>
      </DashboardLayout>
    );
  }

  const hasChanges = name !== organization.name || slug !== organization.slug;

  return (
    <DashboardLayout>
      <div className="max-w-2xl space-y-6">
        {/* Header Card */}
        <Card className="p-6">
          <div className="mb-6 flex items-center gap-3">
            <div className="rounded-lg bg-blue-100 p-2">
              <Briefcase className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Company Settings</h2>
              <p className="text-sm text-gray-500">Manage your company profile and branding</p>
            </div>
          </div>

          {/* Logo Section */}
          <div className="space-y-4 border-b border-gray-200 pb-6">
            <Label className="text-sm font-medium text-gray-700">Company Avatar</Label>
            <div className="flex items-center gap-6">
              <div className="group relative">
                {organization.imageUrl ? (
                  <img
                    src={organization.imageUrl}
                    alt={organization.name}
                    className="h-24 w-24 rounded-lg border-2 border-gray-200 object-cover"
                  />
                ) : (
                  <div className="flex h-24 w-24 items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-100">
                    <Building2 className="h-10 w-10 text-gray-400" />
                  </div>
                )}
                {isUploadingLogo && (
                  <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/50">
                    <Loader2 className="h-6 w-6 animate-spin text-white" />
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleLogoUpload}
                  accept="image/*"
                  className="hidden"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploadingLogo}
                  className="gap-2"
                >
                  <Upload className="h-4 w-4" />
                  Upload Avatar
                </Button>
                {organization.imageUrl && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleRemoveLogo}
                    disabled={isUploadingLogo}
                    className="text-red-600 hover:bg-red-50 hover:text-red-700"
                  >
                    Remove
                  </Button>
                )}
                <p className="text-xs text-gray-500">
                  Recommended: Square image, at least 256x256px
                </p>
              </div>
            </div>
          </div>

          {/* Name and Slug Section */}
          <div className="space-y-4 pt-6">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-sm font-medium text-gray-700">
                Company Name
              </Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter your company name"
                className="max-w-md"
              />
              <p className="text-xs text-gray-500">This is the display name for your company.</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="slug" className="text-sm font-medium text-gray-700">
                Company Slug
              </Label>
              <div className="flex max-w-md items-center gap-2">
                <span className="text-sm text-gray-400">manageclean.com/</span>
                <Input
                  id="slug"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/\s+/g, "-"))}
                  placeholder="your-company"
                  className="flex-1"
                />
              </div>
              <p className="text-xs text-gray-500">
                URL-friendly identifier for your company. Use lowercase letters, numbers, and
                hyphens only.
              </p>
            </div>
          </div>

          {/* Save Button */}
          <div className="mt-6 flex justify-end border-t border-gray-200 pt-6">
            <Button
              onClick={handleSave}
              disabled={!hasChanges || isSaving || !name.trim()}
              className="gap-2"
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </Card>
      </div>
    </DashboardLayout>
  );
}
